import React, { useCallback, useState, useEffect, useContext, useRef, useMemo } from 'react';
import { useDropzone } from 'react-dropzone';
import 'trix';
import 'trix/dist/trix.css';
import Swal from 'sweetalert2';
import { Listbox, Transition } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid';

import { AuthContext } from '../../contexts/auth';
import { patchRichiestaRimborsoInviata, vehiclesList, vehicleCreate } from '../../services/api_dipendenti';

const FUEL_CHOICES = [
  ['benzina', 'Benzina'],
  ['diesel', 'Diesel'],
  ['gpl', 'GPL'],
  ['metano', 'Metano'],
  ['ibrida', 'Ibrida'],
  ['elettrica', 'Elettrica'],
  ['altro', 'Altro'],
];

const UploadFormRimborsoKm = ({
  submitFormKM,          // (FormData) => Promise
  afterSubmit,           // () => void
  onCancelEdit,          // () => void
  recordToEdit,          // record in modifica (con vehicle annidato)
  afterSubmitEditRecord, // (updated) => void
}) => {
  const { token, profile } = useContext(AuthContext);

  // =============== STATE ===============
  const [file, setFile] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [selectedVehicleId, setSelectedVehicleId] = useState('');
  const [loading, setLoading] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const descriptionRef = useRef(null);
  const plateInputRef = useRef(null);
  const vehicleLocked = Boolean(selectedVehicleId);

  const myEmployeeId =
    profile?.employee?.id ??
    profile?.employee_id ??
    profile?.id ??
    null;

  const [formValues, setFormValues] = useState({
    title: '',
    date: '',
    vehicleBrand: '',
    vehicleType: '',
    vehicleMotor: '',
    licensePlate: '',
    departureAddress: '',
    arrivalAddress: '',
    distanceKm: '',
    description: '',
  });

  // =============== HELPERS ===============
  // 1) helper: normalizza targa
  const normalizePlate = (v) => String(v || '').toUpperCase().replace(/\s+/g, '');

  // ...

  // 2) mappa targa → veicolo già salvato
  const vehiclesByPlate = useMemo(() => {
    const map = new Map();
    for (const v of vehicles || []) {
      if (!v?.license_plate) continue;
      map.set(normalizePlate(v.license_plate), v);
    }
    return map;
  }, [vehicles]);

  // ...

  // 3) handle generico dei campi, con auto-fill sul campo targa
  const handleFieldChange = (e) => {
    const { name, value } = e.target;

    if (name === 'licensePlate') {
      const lp = normalizePlate(value);

      // aggiorna SEMPRE il campo targa (normalizzato)
      setFormValues((prev) => ({ ...prev, licensePlate: lp }));
      setFormErrors((prev) => ({ ...prev, licensePlate: '' }));

      // prova a risolvere un veicolo già salvato con questa targa
      const match = vehiclesByPlate.get(lp);

      if (match) {
        // se esiste: seleziona il veicolo nella Listbox e compila i campi
        setSelectedVehicleId(String(match.id));
        setFormValues((prev) => ({
          ...prev,
          // compila i campi SOLO se sono vuoti, per non sovrascrivere input manuali
          vehicleBrand: prev.vehicleBrand || match.vehicle_brand || '',
          vehicleType:  prev.vehicleType  || match.vehicle_type  || '',
          vehicleMotor: prev.vehicleMotor || match.vehicle_motor || '',
        }));
      } else {
        // se non esiste: nessun veicolo selezionato → invieremo vehicle_data
        setSelectedVehicleId('');
        // non svuoto i campi: l’utente potrebbe volerli compilare manualmente
      }

      return; // abbiamo già gestito tutto il caso 'licensePlate'
    }

    // gestione standard degli altri campi
    setFormValues((prev) => ({ ...prev, [name]: value }));
    setFormErrors((prev) => ({ ...prev, [name]: '' }));
  };

  // (facoltativo) forza normalizzazione su blur targa, utile per incollare valori
  const handlePlateBlur = () => {
    setFormValues((prev) => ({ ...prev, licensePlate: normalizePlate(prev.licensePlate) }));
  };

  const clearVehicleFields = () =>
    setFormValues((prev) => ({
      ...prev,
      vehicleBrand: '',
      vehicleType: '',
      vehicleMotor: '',
      licensePlate: '',
    }));

  // =============== FETCH VEICOLI ===============
  useEffect(() => {
    (async () => {
      if (!token || !myEmployeeId) return;
      try {
        const res = await vehiclesList(token, { employee: myEmployeeId, page: 1, pageSize: 100 });
        const list = res?.results ?? [];
        setVehicles(Array.isArray(list) ? list : []);
      } catch (e) {
        console.error(e);
        setVehicles([]);
      }
    })();
  }, [token, myEmployeeId]);

  // =============== EDIT MODE PREFILL ===============
  useEffect(() => {
    if (!recordToEdit) return;

    const v = recordToEdit.vehicle || {};
    setFormValues({
      title: recordToEdit.title || '',
      date: recordToEdit.date || '',
      vehicleBrand: v.vehicle_brand || '',
      vehicleType: v.vehicle_type || '',
      vehicleMotor: v.vehicle_motor || '',
      licensePlate: v.license_plate || '',
      departureAddress: recordToEdit.departure_location || '',
      arrivalAddress: recordToEdit.arrival_location || '',
      distanceKm: recordToEdit.distance_km || '',
      description: recordToEdit.description || '',
    });

    if (v?.id) setSelectedVehicleId(String(v.id));
    if (recordToEdit?.file) setFile(recordToEdit.file);

    setTimeout(() => {
      const editor = document.querySelector('trix-editor');
      if (editor && recordToEdit.description) {
        const sanitized = recordToEdit.description.replace(/https?:\/\/[^\s<]+/g, '');
        editor.editor.loadHTML(sanitized);
      }
    }, 0);
  }, [recordToEdit]);

  const ensureVehicleId = async () => {
    if (selectedVehicleId) return { id: String(selectedVehicleId), created: false };

    const lp = normalizePlate(formValues.licensePlate);
    const existing = vehicles.find(v => v.license_plate === lp);
    if (existing) return { id: String(existing.id), created: false };

    // crea lato server
    try {
      const created = await vehicleCreate(token, {
        license_plate: lp,
        vehicle_brand: (formValues.vehicleBrand || '').trim(),
        vehicle_type: (formValues.vehicleType || '').trim(),
        vehicle_motor: formValues.vehicleMotor || null,
      });
      setVehicles(prev => [created, ...prev]);
      setSelectedVehicleId(String(created.id));
      return { id: String(created.id), created: true };
    } catch (err) {
      const msg =
        err?.response?.data?.license_plate ||
        err?.response?.data?.vehicle_brand ||
        err?.response?.data?.vehicle_type ||
        err?.response?.data?.vehicle_motor ||
        err?.response?.data?.detail ||
        'Errore nel salvataggio del veicolo.';
      await Swal.fire('Errore', Array.isArray(msg) ? msg.join(' ') : String(msg), 'error');
      throw err; // IMPORTANTISSIMO: rilancia → blocca la creazione della richiesta
    }
  };

  // =============== LISTBOX VEICOLI ===============
  const vehiclePlaceholder = { id: '', name: 'Seleziona un veicolo' };
  const vehicleOptions = [
    vehiclePlaceholder,
    ...vehicles.map((v) => ({
      id: String(v.id),
      name: `${v.license_plate} — ${v.vehicle_brand}${v.vehicle_type ? ` ${v.vehicle_type}` : ''}`,
      v,
    })),
  ];
  const selectedVehicleOption =
    vehicleOptions.find((o) => String(o.id) === String(selectedVehicleId)) || vehiclePlaceholder;

  const onPickVehicle = (opt) => {
    const id = opt?.id || '';
    setSelectedVehicleId(id);

    if (opt?.v) {
      const v = opt.v;
      setFormValues((prev) => ({
        ...prev,
        licensePlate: v.license_plate || '',
        vehicleBrand: v.vehicle_brand || '',
        vehicleType: v.vehicle_type || '',
        vehicleMotor: v.vehicle_motor || '',
      }));
    } else {
      clearVehicleFields();
    }
  };

  
  // =============== VALIDAZIONE ===============
  const validateForm = () => {
    const errors = {};
    // campi base
    if (!formValues.title) errors.title = 'Oggetto è richiesto';
    if (!formValues.date) errors.date = 'Data è richiesta';
    if (!formValues.departureAddress) errors.departureAddress = 'Indirizzo Partenza è richiesto';
    if (!formValues.arrivalAddress) errors.arrivalAddress = 'Indirizzo Arrivo è richiesto';
    if (!formValues.distanceKm) errors.distanceKm = 'Distanza Km è richiesta';

    // veicolo: se NON ho scelto un PK, devo avere i dati minimi
    if (!selectedVehicleId) {
      if (!formValues.licensePlate) errors.licensePlate = 'Targa è richiesta';
      if (!formValues.vehicleBrand) errors.vehicleBrand = 'Marca è richiesta';
      if (!formValues.vehicleType) errors.vehicleType = 'Modello è richiesto';
      if (!formValues.vehicleMotor) errors.vehicleMotor = 'Alimentazione è richiesta';
    }

    // descrizione (coerente col comportamento precedente)
    const descriptionText = formValues.description?.replace(/<[^>]*>/g, '').trim();

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // =============== DROPZONE allegato (richiesta) ===============
  const onDrop = useCallback((accepted) => {
    const f = accepted[0];
    if (f?.size > 2 * 1024 * 1024) {
      Swal.fire('Errore', 'File troppo grande. Massimo 2MB', 'warning');
      return;
    }
    if (f) setFile(f);
  }, []);
  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    multiple: false,
    accept: { 'application/pdf': ['.pdf'], 'image/*': [] },
  });
  const handleDeleteFile = () => setFile(null);


  const resetForm = () => {
    setFormValues({
      title: '',
      date: '',
      vehicleBrand: '',
      vehicleType: '',
      vehicleMotor: '',
      licensePlate: '',
      departureAddress: '',
      arrivalAddress: '',
      distanceKm: '',
      description: '',
    });
    setSelectedVehicleId('');
    setFile(null);
    setFormErrors({});
  };

  // =============== SUBMIT: CREATE ===============
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      const { id: vehicleId, created: vehicleCreated } = await ensureVehicleId(); // se fallisce, entra nel catch e NON invia la richiesta

      const fd = new FormData();
      fd.append('title', formValues.title);
      fd.append('date', formValues.date);
      fd.append('departure_location', formValues.departureAddress);
      fd.append('arrival_location', formValues.arrivalAddress);
      fd.append('distance_km', String(formValues.distanceKm));
      fd.append('description', formValues.description || '');
      fd.append('vehicle', vehicleId);
      if (file) fd.append('file_binary', file);

      await submitFormKM(fd);

      await Swal.fire({
        icon: 'success',
        title: 'Successo!',
        text: vehicleCreated
          ? 'Veicolo creato e richiesta inviata correttamente.'
          : 'Richiesta inviata correttamente.',
        timer: 1600,
        showConfirmButton: false,
      });

      // resetForm();
      afterSubmit?.();  
    } catch (error) {
      let msg = 'Errore durante il salvataggio.';
      if (error?.response?.data) {
        const arr = Object.entries(error.response.data).flatMap(([_, v]) => Array.isArray(v) ? v : [v]);
        if (arr.length) msg = arr.join(' ');
      }
      await Swal.fire('Errore', msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  // =============== SUBMIT: EDIT ===============
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    // helper per confronti robusti
    const normStr = (v) => (v ?? '').toString().trim();
    const normNum = (v) => (v === '' || v == null ? null : Number(v));
    const sameStr = (a, b) => normStr(a) === normStr(b);
    const sameNum = (a, b) => {
      const na = normNum(a);
      const nb = normNum(b);
      if (na == null && nb == null) return true;
      return Number(na) === Number(nb);
    };

    const original = recordToEdit || {};
    const originalVehicle = original.vehicle || {};
    const currentVehicleId = originalVehicle?.id ? String(originalVehicle.id) : '';

    const typedPlate = normalizePlate(formValues.licensePlate);
    const originalPlate = normalizePlate(originalVehicle?.license_plate);

    let desiredVehicleId = currentVehicleId;
    let vehicleCreated = false;

    // Caso 1: l'utente ha scelto esplicitamente dalla select
    if (selectedVehicleId && String(selectedVehicleId) !== currentVehicleId) {
      desiredVehicleId = String(selectedVehicleId);
    }
    // Caso 2: l'utente ha digitato una nuova targa diversa dall'originale
    else if (!selectedVehicleId && typedPlate && typedPlate !== originalPlate) {
      try {
        const { id, created } = await ensureVehicleId(); // crea/risolve SOLO se davvero cambiata la targa
        desiredVehicleId = id;
        vehicleCreated = created;
      } catch {
        // ensureVehicleId mostra già lo Swal d'errore → blocca tutto
        return;
      }
    }

    // Costruzione PATCH parziale
    const fd = new FormData();
    let changed = 0;

    // Campi comuni
    if (!sameStr(formValues.title, original.title)) {
      fd.append('title', formValues.title);
      changed++;
    }
    if (!sameStr(formValues.description, original.description)) {
      fd.append('description', formValues.description || '');
      changed++;
    }
    if (!sameStr(formValues.date, original.date)) {
      fd.append('date', formValues.date || '');
      changed++;
    }

    // Campi mileage
    if (!sameStr(formValues.departureAddress, original.departure_location)) {
      fd.append('departure_location', formValues.departureAddress || '');
      changed++;
    }
    if (!sameStr(formValues.arrivalAddress, original.arrival_location)) {
      fd.append('arrival_location', formValues.arrivalAddress || '');
      changed++;
    }
    if (!sameNum(formValues.distanceKm, original.distance_km)) {
      fd.append('distance_km', formValues.distanceKm === '' ? '' : String(formValues.distanceKm));
      changed++;
    }

    // Veicolo: aggiungi solo se è cambiato
    if (desiredVehicleId && desiredVehicleId !== currentVehicleId) {
      fd.append('vehicle', desiredVehicleId);
      changed++;
    }

    // File: sostituzione/rimozione
    // - Se nuovo File: append
    // - Se rimozione (prima c'era, ora no): invia 'file_binary' vuoto
    if (file instanceof File) {
      fd.append('file_binary', file);
      changed++;
    } else if (file === null && original?.file) {
      fd.append('file_binary', ''); // la tua view DRF interpreta '' come rimozione
      changed++;
    }
    // Se file è uguale (URL invariata) → non appendo nulla

    if (changed === 0) {
      await Swal.fire({
        icon: 'info',
        title: 'Nessuna modifica',
        text: 'Non hai modificato alcun campo.',
        timer: 1400,
        showConfirmButton: false,
      });
      return;
    }

    setLoading(true);
    try {
      const updated = await patchRichiestaRimborsoInviata(
        token,
        recordToEdit.id,
        fd,
        'mileage'
      );

      await afterSubmitEditRecord?.(updated);
      await Swal.fire({
        icon: 'success',
        title: 'Salvato!',
        text: vehicleCreated ? 'Veicolo creato e richiesta aggiornata.' : undefined,
        timer: 1200,
        showConfirmButton: false,
      });
    } catch (error) {
      let msg = 'Si è verificato un errore durante l’aggiornamento.';
      if (error?.response?.data) {
        const parts = [];
        Object.entries(error.response.data).forEach(([_, v]) => {
          if (Array.isArray(v)) parts.push(...v);
          else if (typeof v === 'string') parts.push(v);
        });
        if (parts.length) msg = parts.join(' ');
      }
      await Swal.fire({ icon: 'error', title: 'Errore', text: msg });
    } finally {
      setLoading(false);
    }
  };

  const css = `
    .trix-button.trix-button--icon.trix-button--icon-attach { display: none; }
    .trix-button-group.trix-button-group--file-tools { display: none; }
  `;

  return (
    <div>
      <style>{css}</style>

      <form
        encType="multipart/form-data"
        onSubmit={recordToEdit ? handleEditSubmit : handleSubmit}
        className={`px-8 pt-6 pb-8 mb-4 ${recordToEdit ? 'bg-gray-50' : 'bg-white'}`}
      >
        {/* Veicolo associato */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700">Veicolo associato <span className="text-[12px] text-gray-400"> - (Per salvare un nuovo veicolo compila i campi nella richiesta)</span></label>

          <Listbox value={selectedVehicleOption} onChange={onPickVehicle}>
            <div className="relative mt-1">
              <Listbox.Button className="relative h-10 w-full cursor-default rounded-md bg-white py-2 pl-3 pr-10 text-left shadow-sm border border-gray-300 focus:outline-none sm:text-sm">
                <span className="block truncate">
                  {selectedVehicleOption?.name || vehiclePlaceholder.name}
                </span>
                <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                  <ChevronUpDownIcon className="h-5 w-5 text-gray-400" />
                </span>
              </Listbox.Button>
              <Transition leave="transition ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
                <Listbox.Options className="absolute z-30 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm">
                  {vehicleOptions.map((opt) => (
                    <Listbox.Option
                      key={opt.id || 'placeholder'}
                      value={opt}
                      className={({ active }) =>
                        `relative cursor-default select-none py-2 pl-10 pr-4 ${active ? 'bg-indigo-100 text-gray-900' : 'text-gray-900'}`
                      }
                    >
                      {({ selected }) => (
                        <>
                          <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                            {opt.name}
                          </span>
                          {selected && (
                            <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-indigo-800">
                              <CheckIcon className="h-5 w-5" />
                            </span>
                          )}
                        </>
                      )}
                    </Listbox.Option>
                  ))}
                </Listbox.Options>
              </Transition>
              <div className="flex items-center gap-2 mt-2">
                {vehicleLocked && (
                  <button
                    type="button"
                    onClick={() => { setSelectedVehicleId(''); clearVehicleFields()}}
                    className="text-xs font-semibold text-indigo-700 underline"
                  >
                    Cambia veicolo
                  </button>
                )}
              </div>
            </div>
          </Listbox>
        </div>

        {/* Campi standard + campi veicolo (senza rate €/km) */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Oggetto */}
          <div>
            <label className="block text-sm font-medium text-gray-700">Oggetto *</label>
            <input
              type="text"
              name="title"
              value={formValues.title}
              onChange={handleFieldChange}
              className="mt-1 p-2 block w-full border border-gray-300 rounded-md shadow-sm"
            />
            {formErrors.title && <p className="text-red-500 text-xs mt-1">{formErrors.title}</p>}
          </div>

          {/* Data */}
          <div>
            <label className="block text-sm font-medium text-gray-700">Data *</label>
            <input
              type="date"
              name="date"
              value={formValues.date}
              onChange={handleFieldChange}
              className="mt-1 p-2 block w-full border border-gray-300 rounded-md shadow-sm"
            />
            {formErrors.date && <p className="text-red-500 text-xs mt-1">{formErrors.date}</p>}
          </div>

          {/* Targa (se non selezioni un veicolo) */}
          <div>
            <label className="block text-sm font-medium text-gray-700">Targa {selectedVehicleId ? '' : '*'}</label>
            <input
              type="text"
              name="licensePlate"
              value={formValues.licensePlate}
              onChange={handleFieldChange}
              onBlur={handlePlateBlur}
              disabled={vehicleLocked}
              className={`mt-1 p-2 block w-full border rounded-md shadow-sm uppercase placeholder:text-gray-300 disabled:placeholder:text-gray-400 ${
                vehicleLocked ? 'bg-gray-100 cursor-not-allowed border-gray-200' : 'border-gray-300'
              }`}
              placeholder="AB123CD"
            />
            {formErrors.licensePlate && <p className="text-red-500 text-xs mt-1">{formErrors.licensePlate}</p>}
          </div>

          {/* Marca */}
          <div>
            <label className="block text-sm font-medium text-gray-700">Marca {selectedVehicleId ? '' : '*'}</label>
            <input
              type="text"
              name="vehicleBrand"
              value={formValues.vehicleBrand}
              onChange={handleFieldChange}
              disabled={vehicleLocked}
              className={`mt-1 p-2 block w-full border rounded-md shadow-sm ${
                vehicleLocked ? 'bg-gray-100 cursor-not-allowed border-gray-200' : 'border-gray-300'
              }`}
            />
            {formErrors.vehicleBrand && <p className="text-red-500 text-xs mt-1">{formErrors.vehicleBrand}</p>}
          </div>

          {/* Modello */}
          <div>
            <label className="block text-sm font-medium text-gray-700">Modello {selectedVehicleId ? '' : '*'}</label>
            <input
              type="text"
              name="vehicleType"
              value={formValues.vehicleType}
              onChange={handleFieldChange}
              disabled={vehicleLocked}
              className={`mt-1 p-2 block w-full border rounded-md shadow-sm ${
                vehicleLocked ? 'bg-gray-100 cursor-not-allowed border-gray-200' : 'border-gray-300'
              }`}
            />
            {formErrors.vehicleType && <p className="text-red-500 text-xs mt-1">{formErrors.vehicleType}</p>}
          </div>

          {/* Alimentazione (select con choices) */}
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Alimentazione {selectedVehicleId ? '' : '*'}
            </label>

            <Listbox
              value={formValues.vehicleMotor || ''}
              onChange={(val) => handleFieldChange({ target: { name: 'vehicleMotor', value: val } })}
              disabled={vehicleLocked}
            >
              {({ open, disabled }) => (
                <div className="relative mt-1">
                  {/* Button */}
                  <Listbox.Button
                    aria-invalid={!!formErrors.vehicleMotor}
                    className={[
                      'relative w-full rounded-md border py-2 pl-3 pr-10 text-left shadow-sm focus:outline-none',
                      disabled
                        ? 'bg-gray-100 cursor-not-allowed border-gray-200 text-gray-600'
                        : 'bg-white border-gray-300',
                      formErrors.vehicleMotor ? 'border-red-500' : ''
                    ].join(' ')}
                  >
                    <span className="block truncate">
                      {formValues.vehicleMotor
                        ? (FUEL_CHOICES.find(([v]) => v === formValues.vehicleMotor)?.[1] || formValues.vehicleMotor)
                        : '—'}
                    </span>
                    <span className="pointer-events-none absolute inset-y-0 right-2 flex items-center">
                      <svg className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                        <path
                          fillRule="evenodd"
                          d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </span>
                  </Listbox.Button>

                  {/* Options */}
                  <Transition
                    show={open}
                    leave="transition ease-in duration-100"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                  >
                    <Listbox.Options
                      className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1
                                text-sm shadow-lg ring-1 ring-black/10 focus:outline-none"
                    >
                      {/* Opzione vuota */}
                      <Listbox.Option
                        key="__empty__"
                        value=""
                        className={({ active }) =>
                          `relative cursor-default select-none py-2 pl-3 pr-9 ${
                            active ? 'bg-indigo-50 text-indigo-900' : 'text-gray-900'
                          }`
                        }
                      >
                        {({ selected }) => (
                          <>
                            <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>—</span>
                            {selected ? (
                              <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-indigo-600">✓</span>
                            ) : null}
                          </>
                        )}
                      </Listbox.Option>

                      {FUEL_CHOICES.map(([val, label]) => (
                        <Listbox.Option
                          key={val}
                          value={val}
                          className={({ active }) =>
                            `relative cursor-default select-none py-2 pl-3 pr-9 ${
                              active ? 'bg-indigo-50 text-indigo-900' : 'text-gray-900'
                            }`
                          }
                        >
                          {({ selected }) => (
                            <>
                              <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                {label}
                              </span>
                              {selected ? (
                                <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-indigo-600">✓</span>
                              ) : null}
                            </>
                          )}
                        </Listbox.Option>
                      ))}
                    </Listbox.Options>
                  </Transition>
                </div>
              )}
            </Listbox>

            {formErrors.vehicleMotor && (
              <p className="text-red-500 text-xs mt-1">{formErrors.vehicleMotor}</p>
            )}
          </div>

          {/* Partenza / Arrivo / Distanza */}
          <div>
            <label className="block text-sm font-medium text-gray-700">Indirizzo Partenza *</label>
            <input
              type="text"
              name="departureAddress"
              value={formValues.departureAddress}
              onChange={handleFieldChange}
              className="mt-1 p-2 block w-full border border-gray-300 rounded-md shadow-sm"
            />
            {formErrors.departureAddress && <p className="text-red-500 text-xs mt-1">{formErrors.departureAddress}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Indirizzo Arrivo *</label>
            <input
              type="text"
              name="arrivalAddress"
              value={formValues.arrivalAddress}
              onChange={handleFieldChange}
              className="mt-1 p-2 block w-full border border-gray-300 rounded-md shadow-sm"
            />
            {formErrors.arrivalAddress && <p className="text-red-500 text-xs mt-1">{formErrors.arrivalAddress}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Distanza Km *</label>
            <input
              type="number"
              step="0.01"
              min="0"
              name="distanceKm"
              value={formValues.distanceKm}
              onChange={handleFieldChange}
              className="mt-1 p-2 block w-full border border-gray-300 rounded-md shadow-sm"
            />
            {formErrors.distanceKm && <p className="text-red-500 text-xs mt-1">{formErrors.distanceKm}</p>}
          </div>
        </div>

        {/* Allegato richiesta */}
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Allegato</label>

          {recordToEdit?.file && file === recordToEdit.file && (
            <div className="mb-2">
              <span className="text-sm text-gray-700 mr-2">File caricato:</span>
              <a
                href={recordToEdit.file}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-semibold bg-indigo-500 hover:bg-indigo-400 p-1 text-white rounded-md"
              >
                Visualizza
              </a>
              <button type="button" onClick={handleDeleteFile} className="ml-3 text-sm text-red-600 font-semibold underline">
                Elimina
              </button>
            </div>
          )}

          {file && file !== recordToEdit?.file && (
            <div className="mb-2">
              <p className="text-sm text-gray-700">Nuovo file selezionato:</p>
              <ul className="text-sm text-gray-600 list-disc pl-5">
                <li><strong>Nome:</strong> {file.name}</li>
                <li><strong>Tipo:</strong> {file.type}</li>
                <li><strong>Dimensione:</strong> {(file.size / 1024).toFixed(2)} KB</li>
              </ul>
              <button type="button" onClick={handleDeleteFile} className="mt-1 text-red-500 text-sm font-semibold underline">
                Rimuovi
              </button>
            </div>
          )}

          {!file && (
            <div {...getRootProps({ className: 'border-dashed border-2 border-gray-300 p-4 rounded-md cursor-pointer text-center text-sm text-gray-500' })}>
              <input {...getInputProps()} />
              <p>Trascina qui il file o clicca per selezionarlo (max 2MB)</p>
            </div>
          )}
        </div>

        {/* Note (TRIX) */}
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Note aggiuntive *</label>
          <input
            type="hidden"
            id="description"
            name="description"
            ref={descriptionRef}
            value={formValues.description}
            onChange={() => {}}
          />
          <trix-editor
            input="description"
            onChange={(e) => setFormValues((p) => ({ ...p, description: e.target.innerHTML }))}
            onBlur={() => {
              const html = descriptionRef.current?.value;
              setFormValues((p) => ({ ...p, description: html || p.description }));
            }}
          />
          {formErrors.description && <p className="text-red-500 text-xs mt-1">{formErrors.description}</p>}
        </div>

        {/* Submit */}
        <div className="mt-6 flex justify-center gap-3">
          {loading ? (
            <div className="text-sm font-semibold text-gray-900">Salvataggio in corso...</div>
          ) : recordToEdit ? (
            <>
              <button
                type="submit"
                className="bg-indigo-600 text-white text-sm px-4 py-2 rounded-md hover:bg-indigo-500 font-semibold"
              >
                Aggiorna
              </button>
              <button
                type="button"
                onClick={onCancelEdit}
                className="text-sm font-semibold bg-gray-200 hover:bg-gray-300 px-4 py-2 text-gray-700 rounded-md"
              >
                Annulla
              </button>
            </>
          ) : (
            <button
              type="submit"
              className="bg-indigo-600 text-white text-sm px-4 py-2 rounded-md hover:bg-indigo-500 font-semibold"
            >
              Invia
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default UploadFormRimborsoKm;